import { useNavigate, useSearchParams } from 'react-router-dom'
import type { InventoryItemNameRow } from '@/lib/api'
import { useStorefrontCategories } from '@/hooks/useStorefrontCategories'
import MenuSelect from '@/components/ui/MenuSelect'

const triggerClass =
  'py-2 pl-3 pr-2 text-xs font-semibold text-stone-800 hover:text-primary-700 sm:pl-4 sm:pr-3 sm:text-sm'

/** Select value for “My Favorite” (not a real category id). */
const CATEGORY_SELECT_FAVORITES = '__favorites__'

type Props = {
  inventoryItems?: InventoryItemNameRow[]
  inventoryLoading?: boolean
}

/**
 * Category picker that sits inside the left edge of the header search box.
 * Only lists categories that have ≥1 in-stock item.
 */
export default function StoreFilterBar({
  inventoryItems = [],
  inventoryLoading = false,
}: Props) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { visibleCategories } = useStorefrontCategories(inventoryItems, inventoryLoading)

  const categoryId = searchParams.get('category') ?? ''
  const favoritesOnly = searchParams.get('favorites') === '1'

  const selectValue = favoritesOnly
    ? CATEGORY_SELECT_FAVORITES
    : visibleCategories.some((c) => c.category_id === categoryId)
      ? categoryId
      : ''

  const goItemsWithCategory = (categoryValue: string) => {
    const next = new URLSearchParams(searchParams)
    next.delete('category')
    next.delete('favorites')
    next.delete('deals')
    next.delete('page')
    if (categoryValue === CATEGORY_SELECT_FAVORITES) {
      next.set('favorites', '1')
    } else if (categoryValue) {
      next.set('category', categoryValue)
    }
    navigate({ pathname: '/items', search: next.toString() })
  }

  const options = [
    { value: '', label: 'All' },
    { value: CATEGORY_SELECT_FAVORITES, label: 'My Favorite' },
    ...visibleCategories.map((c) => ({ value: c.category_id, label: c.name })),
  ]

  return (
    <MenuSelect
      value={selectValue}
      options={options}
      onChange={goItemsWithCategory}
      ariaLabel="Categories"
      title="Filter items by category or favorites"
      // Left corners match the search box's inner radius (its border is 2px).
      className="w-[6.5rem] shrink-0 self-stretch rounded-l-[6px] border-r border-stone-200 bg-stone-100/80 hover:bg-stone-100 sm:w-[11rem] sm:rounded-l-[10px]"
      buttonClassName={triggerClass}
    />
  )
}
